import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import {
  Phone,
  WhatsApp,
  CheckCircle,
  EventRepeat,
  AddIcCall,
  Payments,
  PrecisionManufacturing,
  Refresh,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { CommercialToday } from '../types';
import ScheduleCallDialog from '../components/commercial/ScheduleCallDialog';
import CompleteCallDialog from '../components/commercial/CompleteCallDialog';
import { PendingDeliveryCards } from '../components/commercial/PendingDeliveriesDialog';
import { formatDay, formatMoney, telUrl, whatsappUrl } from '../components/commercial/contact';

interface Props {
  /** Prefijo de rutas para los botones de acción (modo celular usa "/m"). */
  basePath?: string;
}

const bigBtn = { minHeight: 48, fontSize: 16, flex: 1, minWidth: 120 } as const;

const SectionTitle: React.FC<{ title: string; count: number; color?: string }> = ({ title, count, color }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 3, mb: 1.5 }}>
    <Typography variant="h6" fontWeight={700}>{title}</Typography>
    <Chip size="small" label={count} sx={{ bgcolor: color, color: color ? '#fff' : undefined, fontWeight: 700 }} />
  </Stack>
);

const ContactButtons: React.FC<{ phone?: string | null; whatsApp?: string | null; text?: string }> = ({ phone, whatsApp, text }) => {
  const wa = whatsappUrl(whatsApp || phone, text);
  const tel = telUrl(phone || whatsApp);
  return (
    <>
      {wa && (
        <Button variant="contained" color="success" startIcon={<WhatsApp />} href={wa} target="_blank" rel="noopener" sx={bigBtn}>
          WhatsApp
        </Button>
      )}
      {tel && (
        <Button variant="outlined" startIcon={<Phone />} href={tel} sx={bigBtn}>
          Llamar
        </Button>
      )}
    </>
  );
};

const TodayPage: React.FC<Props> = ({ basePath = '' }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<CommercialToday | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scheduleFor, setScheduleFor] = useState<{ id: number; name: string } | null>(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [completing, setCompleting] = useState<{ id: number; name: string } | null>(null);
  const [route, setRoute] = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.getCommercialToday());
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo cargar el día');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const postpone = async (callId: number) => {
    try {
      await api.rescheduleCall(callId, { inDays: 1 });
      load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo pasar a mañana');
    }
  };

  const openSchedule = (c: { id: number; name: string } | null) => {
    setScheduleFor(c);
    setScheduleOpen(true);
  };

  if (loading && !data) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  // Filtro por recorrida/zona: para salir a visitar o repartir por barrio.
  const routes = Array.from(new Set([...(data?.calls || []), ...(data?.suggestedCalls || []), ...(data?.collections || [])]
    .map(x => x.route).filter((r): r is string => !!r))).sort();
  const inRoute = (x: { route?: string | null }) => !route || (route === '__none' ? !x.route : x.route === route);
  const calls = (data?.calls || []).filter(inRoute);
  const suggested = (data?.suggestedCalls || []).filter(inRoute);
  const collections = (data?.collections || []).filter(inRoute);
  const stock = data?.stockAlerts || [];
  const nothing = calls.length + suggested.length + collections.length + stock.length === 0;

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Hoy</Typography>
          <Typography variant="body2" color="text.secondary">
            {data ? formatDay(data.today) : ''}
            {data && data.upcomingCallsCount > 0 ? ` · ${data.upcomingCallsCount} llamados agendados más adelante` : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" startIcon={<AddIcCall />} onClick={() => openSchedule(null)} sx={{ minHeight: 44 }}>
            Agendar
          </Button>
          <Button variant="outlined" onClick={load} disabled={loading} sx={{ minHeight: 44, minWidth: 44 }} aria-label="Actualizar">
            <Refresh />
          </Button>
        </Stack>
      </Stack>

      {routes.length > 0 && (
        <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
          <Chip label="Todas las zonas" color={!route ? 'primary' : 'default'} onClick={() => setRoute('')} />
          {routes.map(r => <Chip key={r} label={r} color={route === r ? 'primary' : 'default'} onClick={() => setRoute(r)} />)}
          <Chip label="Sin zona" color={route === '__none' ? 'primary' : 'default'} variant="outlined" onClick={() => setRoute('__none')} />
        </Stack>
      )}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {nothing && !error && (
        <Alert severity="success" sx={{ mt: 3 }}>Nada pendiente para hoy. 🎉</Alert>
      )}

      {calls.length > 0 && (
        <>
          <SectionTitle title="Llamar hoy" count={calls.length} color="#0ea5e9" />
          <Stack spacing={1.5}>
            {calls.map(c => (
              <Card key={c.id} variant="outlined" sx={{ borderLeft: 6, borderLeftColor: c.isOverdue ? 'error.main' : '#0ea5e9' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>{c.customerName}{c.route ? <Chip size="small" label={c.route} sx={{ ml: 1 }} /> : null}</Typography>
                      {c.notes && <Typography variant="body1" sx={{ mt: 0.5 }}>{c.notes}</Typography>}
                    </Box>
                    {c.isOverdue && <Chip color="error" size="small" label={`Atrasado ${c.daysOverdue} d`} />}
                  </Stack>
                  <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
                    <ContactButtons phone={c.phone} whatsApp={c.whatsApp} />
                    <Button variant="contained" color="primary" startIcon={<CheckCircle />} sx={bigBtn}
                      onClick={() => setCompleting({ id: c.id, name: c.customerName })}>
                      Hecho
                    </Button>
                    <Button variant="text" startIcon={<EventRepeat />} sx={bigBtn} onClick={() => postpone(c.id)}>
                      Mañana
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </>
      )}

      {collections.length > 0 && (
        <>
          <SectionTitle title="A cobrar" count={collections.length} color="#16a34a" />
          <Stack spacing={1.5}>
            {collections.map(c => (
              <Card key={c.customerId} variant="outlined" sx={{ borderLeft: 6, borderLeftColor: c.daysOverdue > 0 ? 'error.main' : 'success.main' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>{c.customerName}{c.route ? <Chip size="small" label={c.route} sx={{ ml: 1 }} /> : null}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {c.orderCount} {c.orderCount === 1 ? 'venta' : 'ventas'} · vence {formatDay(c.oldestDueDate)}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="h6" fontWeight={800}>{formatMoney(c.amountDue)}</Typography>
                      {c.daysOverdue > 0
                        ? <Chip color="error" size="small" label={`Vencido ${c.daysOverdue} d`} />
                        : <Chip color="warning" size="small" label="Por vencer" />}
                    </Box>
                  </Stack>
                  <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
                    <ContactButtons
                      phone={c.phone}
                      whatsApp={c.whatsApp}
                      text={`Hola! Te escribo por el saldo pendiente de ${formatMoney(c.amountDue)}. ¿Cuándo te queda bien abonarlo?`}
                    />
                    <Button variant="contained" startIcon={<Payments />} sx={bigBtn}
                      onClick={() => navigate(basePath ? `${basePath}/cobrar?customerId=${c.customerId}` : `/customers?account=${c.customerId}`)}>
                      Registrar cobro
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </>
      )}

      {suggested.length > 0 && (
        <>
          <SectionTitle title="Les toca volver a comprar" count={suggested.length} color="#7c3aed" />
          <Stack spacing={1.5}>
            {suggested.map(s => (
              <Card key={s.customerId} variant="outlined" sx={{ borderLeft: 6, borderLeftColor: '#7c3aed' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700}>{s.customerName}{s.route ? <Chip size="small" label={s.route} sx={{ ml: 1 }} /> : null}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Compra cada ~{s.avgDaysBetweenOrders} días · última {formatDay(s.lastOrderAt)}
                    {s.daysLate > 0 ? ` · ${s.daysLate} días de atraso` : ' · le toca hoy'}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
                    <ContactButtons phone={s.phone} whatsApp={s.whatsApp} text="Hola! ¿Cómo andás? Te escribo para ver si necesitás reponer." />
                    <Button variant="outlined" startIcon={<AddIcCall />} sx={bigBtn}
                      onClick={() => openSchedule({ id: s.customerId, name: s.customerName })}>
                      Agendar
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </>
      )}

      <PendingDeliveryCards hideWhenEmpty title={n => <SectionTitle title="Pedidos a entregar" count={n} color="#16a34a" />} />

      {stock.length > 0 && (
        <>
          <SectionTitle title="Stock por quebrar" count={stock.length} color="#ea580c" />
          <Stack spacing={1.5}>
            {stock.map(s => (
              <Card key={s.productId} variant="outlined" sx={{ borderLeft: 6, borderLeftColor: s.level === 'critical' ? 'error.main' : 'warning.main' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>{s.productName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Quedan {s.currentStock} {s.unitOfMeasure}
                        {s.dailySales > 0 ? ` · se venden ~${s.dailySales}/día` : ''}
                        {s.belowMinStock ? ` · bajo el mínimo (${s.minStock})` : ''}
                      </Typography>
                    </Box>
                    <Chip
                      color={s.level === 'critical' ? 'error' : 'warning'}
                      label={s.daysOfCoverage != null ? `~${s.daysOfCoverage} días` : 'Bajo mínimo'}
                      sx={{ fontWeight: 700 }}
                    />
                  </Stack>
                  {s.hasRecipe && (
                    <Stack direction="row" sx={{ mt: 1.5 }}>
                      <Button variant="contained" color="warning" startIcon={<PrecisionManufacturing />} sx={bigBtn}
                        onClick={() => navigate(basePath
                          ? `${basePath}/producir?productId=${s.productId}&qty=${s.suggestedProduction}`
                          : '/production-orders')}>
                        Producir {s.suggestedProduction > 0 ? `${s.suggestedProduction} ${s.unitOfMeasure}` : 'lote'}
                      </Button>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            ))}
          </Stack>
        </>
      )}

      <ScheduleCallDialog
        open={scheduleOpen}
        customer={scheduleFor}
        onClose={() => setScheduleOpen(false)}
        onScheduled={load}
      />
      <CompleteCallDialog
        open={!!completing}
        callId={completing?.id ?? null}
        customerName={completing?.name}
        onClose={() => setCompleting(null)}
        onDone={load}
      />
    </Box>
  );
};

export default TodayPage;
